import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { $createCodeNode } from "@lexical/code";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createQuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND } from "lexical";
import {
  Bold,
  Code,
  FileCode,
  Italic,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
} from "lucide-react";
import { useEffect, useState } from "react";

interface ToolbarProps {
  readonly?: boolean;
}

interface FormatState {
  bold: boolean;
  italic: boolean;
  strikethrough: boolean;
  code: boolean;
}

export function SlackToolbar({ readonly = false }: ToolbarProps) {
  const [editor] = useLexicalComposerContext();
  const [formatState, setFormatState] = useState<FormatState>({
    bold: false,
    italic: false,
    strikethrough: false,
    code: false,
  });

  // Update format state when selection changes
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setFormatState({
            bold: selection.hasFormat("bold"),
            italic: selection.hasFormat("italic"),
            strikethrough: selection.hasFormat("strikethrough"),
            code: selection.hasFormat("code"),
          });
        }
      });
    });
  }, [editor]);

  const formatText = (format: "bold" | "italic" | "strikethrough" | "code") => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const insertCodeBlock = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createCodeNode());
      }
    });
  };

  const insertQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  if (readonly) return null;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1 p-2 border-b border-neutral-200 dark:border-neutral-800 bg-card">
        <ToolbarButton
          onClick={() => formatText("bold")}
          icon={<Bold className="w-4 h-4" />}
          label="Bold (*text*)"
          active={formatState.bold}
        />
        <ToolbarButton
          onClick={() => formatText("italic")}
          icon={<Italic className="w-4 h-4" />}
          label="Italic (_text_)"
          active={formatState.italic}
        />
        <ToolbarButton
          onClick={() => formatText("strikethrough")}
          icon={<Strikethrough className="w-4 h-4" />}
          label="Strikethrough (~text~)"
          active={formatState.strikethrough}
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton
          onClick={() => formatText("code")}
          icon={<Code className="w-4 h-4" />}
          label="Inline Code (`code`)"
          active={formatState.code}
        />
        <ToolbarButton
          onClick={insertCodeBlock}
          icon={<FileCode className="w-4 h-4" />}
          label="Code Block (```)"
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
          }
          icon={<List className="w-4 h-4" />}
          label="Bullet List"
        />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
          }
          icon={<ListOrdered className="w-4 h-4" />}
          label="Numbered List"
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton
          onClick={insertQuote}
          icon={<Quote className="w-4 h-4" />}
          label="Quote (>)"
        />
      </div>
    </TooltipProvider>
  );
}

interface ToolbarButtonProps {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  icon,
  label,
  active = false,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClick}
          className={cn(
            "h-8 w-8 p-0",
            active && "bg-neutral-200 dark:bg-neutral-700",
          )}
          aria-label={label}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
};
